import { useEffect, useState, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Stack,
  TextField,
  Toggle,
  IconButton,
  TooltipHost,
  Label,
} from "@fluentui/react";
import "../../css/dropDownTree.css";
import { useLogin } from "../../redux/hooks/useLogin";
import { useCheckin } from "../../redux/hooks/useCheckin";
import { useMetadata } from "../../redux/hooks/useMetadata";
import { useDocuments } from "../../redux/hooks/useDocuments";
import { useOneDrive } from "../../redux/hooks/useOneDrive";
import { useTools } from "../../redux/hooks/useTools";
import {
  CREATE_VIEW_COPY_SUPPORT,
  DATA_LOAD_STATUS,
  DOC_CHECK_IN_TYPE,
  FILE_AUTO_ENABLE,
  FILE_AUTO_ENABLE_SUB,
  REFRESH_TOKEN_ACTION,
  RESPONSE_TYPE,
} from "../../redux/constants/metaData";
import {
  RELEASE_DOCUMENT,
  RELEASE_DOCUMENT_TOOLTIP,
  DELETE_ONEDRIVE_DOC,
  DELETE_ONEDRIVE_DOC_TOOLTIP,
} from "../../redux/constants/labelText";
import ViewCopyDragDropZone from "./ViewCopyDragDropZone";
import ViewCopyRecord from "./ViewCopyRecord";
import { useTheme } from "../../theme/OAS365Theme";
import { useCommonControlsStyle } from "../../theme/styles/CommonControlsStyle";
import { useUploadFilesItem } from "../../theme/styles/UploadFilesItemStyle";
import ClassFormatTreeLoad from "./ClassFormatTreeLoad";
import infoIcon from "../../assets/icons/information.svg";
import {
  Box,
  Typography,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Tabs,
  Tab,
} from "@mui/material";
import docChecked from "../../assets/workingDocIcons/docChecked.svg";
import docDelete from "../../assets/workingDocIcons/docDelete.svg";
import Tooltip from "@mui/material/Tooltip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Favorite from "@mui/icons-material/Favorite";
import Search from "@mui/icons-material/Search";
import { useViewCopyStyle } from "../../theme/styles/ViewCopyStyle";
import { isFileExtValid } from "../../functions/helperMethod";
import {
  getFileTypeMismatchError,
  CHECKIN_VALIDATION_TITLE_REQUIRED,
  CHECKIN_VALIDATION_VIEW_COPY_REQUIRED,
  CHECKIN_VALIDATION_OBJECT_CONNECTION_REQUIRED,
} from "../../redux/constants/messageTypes";
import { isValidPlayload } from "../../functions/reduxMethod";
import { getDisplayFileName } from "../../functions/helperMethod";
import SelectedObjectConnection from "./SelectedObjectConnection";
import RecentObjectsView from "./RecentObjectsView";
import AdvSearchObjectConnectionTab from "./AdvSearchObjectConnectionTab";
import FieldValidationMessage from "./FieldValidationMessage";
import {
  AccordionSummaryWithValidation,
  getAccordionSummarySx,
} from "./AccordionSummaryWithValidation";

const DocumentBasicItem = ({
  onTreeDataLoadingChange,
  objectConnectionTabKey,
  onObjectConnectionTabKeyChange,
  tabKey,
  /** When true, show inline red validation messages under required fields. */
  showFieldValidation = true,
  /** Bulk check-in: no single-upload title; Redux title may not apply. */
  omitTitleSection = false,
  /** Bulk check-in: hide optional OneDrive delete banner (IFS row context may not match bulk list). */
  omitOneDriveDeletionRow = false,
  /** Bulk Settings: View Copy with auto-generate toggle only (no PDF upload). */
  bulkSettingsViewCopy = false,
  /** While IFS browse auto-population loads the class, show the class combo spinner. */
  isAutopopulating = false,
}) => {
  const location = useLocation();
  const selectedDocumentFromList = location.state?.selectedDocument ?? null;

  const { teamsContext } = useLogin();
  const {
    docTitle,
    docClass,
    docReleaseStatus,
    createViewCopyDisable,
    createViewCopy,
    uploadFiles,
    uploadViewCopyCount,
    viewRequired,
    objConRequired,
    viewCopyFiles,
    docFileTypeList,
    selectedObjectConnections,
    onObjectConnectionsLoad,
  } = useCheckin();
  const { rowSelectionDetails, objectConnectionDataLoad, logicalUnit } =
    useMetadata();
  const { ifsLatestDocument } = useDocuments();
  const {
    onDocTitleChange,
    onDocReleaseChange,
    onCreateViewCopyDisable,
    onCreateViewCopySelect,
    onSetDocumentToDeleteOneDrive,
    onAllowSelectionChange,
    onViewCopyUploadChange,
  } = useTools();
  const oneDriveDocumentId = ifsLatestDocument.oneDriveDocumentId;
  const oneDriveDeleteRequired = ifsLatestDocument.oneDriveDeleteRequired;
  const isAllowSelection = ifsLatestDocument.isAllowSelection;
  const theme = useTheme();
  const styleTheme = useCommonControlsStyle();
  const fileStyleTheme = useUploadFilesItem();
  const accordianThemeStyle = useViewCopyStyle();
  const binIcon = { iconName: "Info" };
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const [objectConAccordionExpanded, setObjectConAccordionExpanded] =
    useState(false);
  const [showSelectedNewIndicator, setShowSelectedNewIndicator] =
    useState(false);
  const [userManuallyClosed, setUserManuallyClosed] = useState(false);
  const previousClassRef = useRef(docClass);
  const previousViewRequiredRef = useRef(
    viewRequired === true || viewRequired === "Y",
  );
  const defaultOneDriveDeleteInitializedFor = useRef(null);
  const [isCurrentFileTypeValid, setIsCurrentFileTypeValid] = useState(true);

  // Default "Delete OneDrive original" to checked once per list selection (user may uncheck after).
  // Browse files are local — never treat their synthetic key as a Graph item id.
  useEffect(() => {
    const docKey = selectedDocumentFromList?.key;
    if (
      !docKey ||
      ifsLatestDocument.checkinType !== DOC_CHECK_IN_TYPE.checkinNew
    ) {
      return;
    }
    if (defaultOneDriveDeleteInitializedFor.current === docKey) {
      return;
    }
    defaultOneDriveDeleteInitializedFor.current = docKey;
    if (selectedDocumentFromList?.source === "browse") {
      onSetDocumentToDeleteOneDrive({
        checkinSuccess: false,
        id: null,
        deleteRequired: false,
        checkinType: DOC_CHECK_IN_TYPE.checkinNew,
      });
      return;
    }
    onSetDocumentToDeleteOneDrive({
      checkinSuccess: false,
      id: docKey,
      deleteRequired: true,
      checkinType: DOC_CHECK_IN_TYPE.checkinNew,
    });
  }, [
    selectedDocumentFromList?.key,
    selectedDocumentFromList?.source,
    ifsLatestDocument.checkinType,
    onSetDocumentToDeleteOneDrive,
  ]);

  const handleReleaseChange = (event) => {
    onDocReleaseChange(event.target.checked);
  };
  const isChecked = docReleaseStatus === "Released";
  const isDeleteOneDriveChecked = Boolean(oneDriveDeleteRequired);

  const handleDeleteOneDriveChange = (event) => {
    const selectedId =
      rowSelectionDetails?.[0]?.key ??
      oneDriveDocumentId ??
      selectedDocumentFromList?.key;

    if (!selectedId) {
      return;
    }

    onSetDocumentToDeleteOneDrive({
      checkinSuccess: false,
      id: selectedId,
      deleteRequired: event.target.checked,
      checkinType: DOC_CHECK_IN_TYPE.checkinNew,
    });
  };

  function getViewCopyToolTip() {
    if (bulkSettingsViewCopy) {
      return "";
    }
    if (
      uploadFiles !== undefined &&
      uploadFiles !== null &&
      uploadFiles.length > 0
    ) {
      let title = uploadFiles[0].name;
      let ext = uploadFiles[0].name;
      title = title.replace(/\.[^/.]+$/, "");
      ext = ext.split(".").pop().toUpperCase();
      return ext === "DOCX" || ext === "DOC" || ext === "DOCM"
        ? ""
        : CREATE_VIEW_COPY_SUPPORT;
    }
    return CREATE_VIEW_COPY_SUPPORT;
  }

  const isWordDocument = () => {
    if (!uploadFiles || uploadFiles.length === 0) return false;

    const fileName = uploadFiles[0].name;
    const ext = fileName.split(".").pop().toUpperCase();
    return ext === "DOCX" || ext === "DOC" || ext === "DOCM";
  };

  const shouldShowAutoGenerateToggle = isWordDocument();

  const isViewRequired =
    viewRequired === true || viewRequired === "Y";
  const hasPrimaryUploadFile =
    Array.isArray(uploadFiles) && uploadFiles.length > 0 && !!uploadFiles[0]?.name;
  const shouldValidateCurrentFileType =
    !!String(docClass ?? "").trim() && hasPrimaryUploadFile;

  /** Bulk Settings: always show toggle when View Copy is expanded. */
  const showAutoGeneratePanel = bulkSettingsViewCopy
    ? true
    : shouldShowAutoGenerateToggle;

  const isUploadDisabled = () => {
    return showAutoGeneratePanel && createViewCopy === true;
  };

  useEffect(() => {
    if (!bulkSettingsViewCopy) {
      return;
    }
    onCreateViewCopyDisable(false);
    if (!isViewRequired) {
      onCreateViewCopySelect(false, { programmatic: true });
    }
  }, [
    bulkSettingsViewCopy,
    isViewRequired,
    onCreateViewCopyDisable,
    onCreateViewCopySelect,
  ]);

  useEffect(() => {
    if (!shouldValidateCurrentFileType) {
      setIsCurrentFileTypeValid(true);
      return;
    }

    setIsCurrentFileTypeValid(isFileExtValid(docFileTypeList, uploadFiles));
  }, [shouldValidateCurrentFileType, docFileTypeList, uploadFiles]);

  useEffect(() => {
    const classChanged = previousClassRef.current !== docClass;

    if (bulkSettingsViewCopy) {
      const viewRequiredChanged =
        previousViewRequiredRef.current !== isViewRequired;

      if (isViewRequired && (!userManuallyClosed || classChanged)) {
        setIsAccordionExpanded(true);
        if (classChanged) {
          setUserManuallyClosed(false);
        }
      } else if (
        !isViewRequired &&
        (classChanged || viewRequiredChanged)
      ) {
        // Class/rules say view copy not required: collapsed by default.
        // User may still expand manually via the accordion header.
        setIsAccordionExpanded(false);
        setUserManuallyClosed(false);
      }

      previousClassRef.current = docClass;
      previousViewRequiredRef.current = isViewRequired;
      return;
    }

    if (viewRequired) {
      if (!userManuallyClosed || classChanged) {
        setIsAccordionExpanded(true);
        if (classChanged) {
          setUserManuallyClosed(false);
        }
      }
    } else {
      setIsAccordionExpanded(false);
      setUserManuallyClosed(false);
    }

    previousClassRef.current = docClass;
  }, [bulkSettingsViewCopy, isViewRequired, viewRequired, docClass, userManuallyClosed]);

  const handleAccordionChange = (event, isExpanded) => {
    setIsAccordionExpanded(isExpanded);
    if (!isExpanded) {
      setUserManuallyClosed(true);
    } else {
      setUserManuallyClosed(false);
    }
  };

  const handleObjectConAccordionChange = (_event, isExpanded) => {
    setObjectConAccordionExpanded(isExpanded);
  };

  const selectedObjectConnectionCount = useMemo(
    () =>
      Array.isArray(selectedObjectConnections)
        ? selectedObjectConnections.length
        : 0,
    [selectedObjectConnections],
  );

  const logicalUnitsBootstrapRef = useRef(false);

  useEffect(() => {
    if (objectConnectionTabKey === tabKey.checkinNewSelectedObj) {
      setShowSelectedNewIndicator(false);
    }
  }, [objectConnectionTabKey, tabKey.checkinNewSelectedObj]);

  useEffect(() => {
    if (objectConnectionDataLoad !== DATA_LOAD_STATUS.onStart) {
      logicalUnitsBootstrapRef.current = false;
      return;
    }
    if (!teamsContext) {
      return;
    }
    if (logicalUnitsBootstrapRef.current) {
      return;
    }
    logicalUnitsBootstrapRef.current = true;
    onObjectConnectionsLoad(teamsContext, {
      retry: 0,
      actionType: REFRESH_TOKEN_ACTION.logicalUnitsLoad,
      refreshResponse: RESPONSE_TYPE.none,
    });
    // Recent objects load when user opens the Favorites tab (RecentObjectsView).
  }, [objectConnectionDataLoad, teamsContext, onObjectConnectionsLoad]);

  const handleObjectConnectionTabChange = (tabValue) => {
    onObjectConnectionTabKeyChange(tabValue);
    if (tabValue !== tabKey.checkinNewAdvanceObj || !teamsContext) {
      return;
    }

    const hasUnits = Array.isArray(logicalUnit) && logicalUnit.length > 0;
    if (hasUnits) {
      return;
    }

    const isLoading =
      objectConnectionDataLoad === DATA_LOAD_STATUS.onStart ||
      objectConnectionDataLoad === DATA_LOAD_STATUS.onLoad;
    if (isLoading) {
      return;
    }

    if (
      objectConnectionDataLoad !== DATA_LOAD_STATUS.onStart &&
      objectConnectionDataLoad !== DATA_LOAD_STATUS.error
    ) {
      return;
    }

    onObjectConnectionsLoad(teamsContext, {
      retry: 0,
      actionType: REFRESH_TOKEN_ACTION.logicalUnitsLoad,
      refreshResponse: RESPONSE_TYPE.none,
    });
  };

  ///const fileExtension = getFileExtension(uploadFiles);
  // const getFileExtension = (uploadFiles) => {
  //   if(isValidPlayload(uploadFiles) && uploadFiles.length > 0){
  //     const fileName = uploadFiles[0].name;
  //     return getFileExtension(fileName);
  //   }
  //   return "";
  // }

  const hasUploadedFiles = uploadViewCopyCount > 0;
  const leftBoxHeight = "147px";
  const rightBoxHeight = "180px";
  const uploadBoxHeight = hasUploadedFiles ? "110px" : rightBoxHeight;

  const titleError =
    showFieldValidation &&
    !omitTitleSection &&
    (!docTitle || !String(docTitle).trim())
      ? CHECKIN_VALIDATION_TITLE_REQUIRED
      : "";
  const viewCopyError =
    showFieldValidation &&
    isViewRequired &&
    uploadViewCopyCount === 0 &&
    createViewCopy !== true
      ? CHECKIN_VALIDATION_VIEW_COPY_REQUIRED
      : "";
  const objectConnectionError =
    showFieldValidation &&
    objConRequired === true &&
    (!selectedObjectConnections || selectedObjectConnections.length === 0)
      ? CHECKIN_VALIDATION_OBJECT_CONNECTION_REQUIRED
      : "";

  const uploadViewCopyFiles =
    createViewCopy === false ? (
      <>
        <Stack disableShrink tokens={{ childrenGap: 10 }}>
          <Stack.Item styles={fileStyleTheme.ViewCopyStackStyle}>
            <span
              style={{
                fontFamily: theme.fonts.regular,
                fontSize: theme.fontSizes.normal,
              }}
            >
              Upload View Copy
            </span>
            <TooltipHost
              width={50}
              content={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <img
                    src={infoIcon}
                    alt="info"
                    style={{
                      width: "14px",
                    }}
                  ></img>
                  <div style={{ flexDirection: "column" }}>
                    <p style={{ marginLeft: 4 }}>
                      Only supports PDF documents.
                    </p>
                  </div>
                </div>
              }
              tooltipProps={{
                calloutProps: {
                  styles: {
                    fontColor: theme.colors.addovationWhite,
                    beak: {
                      background: theme.colors.addovation70Green,
                      color: theme.colors.addovationWhite,
                    },
                    beakCurtain: {
                      background: theme.colors.addovation70Green,
                      color: theme.colors.addovationWhite,
                    },
                    calloutMain: {
                      background: theme.colors.addovation70Green,
                      color: theme.colors.addovationWhite,
                    },
                  },
                },
                styles: {
                  content: {
                    color: theme.colors.addovationWhite,
                  },
                },
              }}
            >
              <IconButton
                style={{ marginLeft: "10px", height: "5px", width: "5px" }}
                iconProps={binIcon}
                title="Info"
                ariaLabel="Info"
              />
            </TooltipHost>

            <ViewCopyDragDropZone />
          </Stack.Item>{" "}
          <Stack.Item>
            <ViewCopyRecord />
          </Stack.Item>
        </Stack>
      </>
    ) : (
      <></>
    );

  return (
    <>
      <Stack
        style={{ marginTop: "10px" }}
        horizontalAlign="center"
        verticalAlign="center"
        tokens={{ childrenGap: 5 }}
      >
        <Stack.Item style={{ marginTop: "5px", width: "100%" }}>
          {!omitTitleSection && (
            <Box sx={{ width: "100%" }}>
              <span
                style={{
                  fontFamily: theme.fonts.regular,
                  fontSize: theme.fontSizes.normal,
                  color: theme.colors.fontColor,
                }}
              >
                Title
                <span> *</span>
              </span>
              <TextField
                styles={{
                  ...styleTheme.TextFiledsStyle,
                  field: {
                    ...(styleTheme.TextFiledsStyle?.field ?? {}),
                    fontFamily: theme.fonts?.regular,
                    fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                  },
                }}
                value={getDisplayFileName(docTitle)}
                placeholder="Enter Text Here..."
                onChange={(ev, item) => onDocTitleChange(item)}
              />
              <FieldValidationMessage message={titleError} />
              {shouldValidateCurrentFileType && !isCurrentFileTypeValid && (
                <FieldValidationMessage message={getFileTypeMismatchError()} />
              )}
            </Box>
          )}
          {/* {!isCurrentFileTypeValid && (<Stack.Item grow align="center" style={{ marginTop: "2px" }}>
           <Label
              style={{ fontSize: "small", color: "red", textAlign: "center" }}
           >
             {!isCurrentFileTypeValid && getFileTypeMismatchError(getFileExtension(uploadFiles))}
            </Label>
          </Stack.Item>)} */}

          <Stack.Item
            grow
            align="center"
            style={{ marginTop: omitTitleSection ? "5px" : "25px" }}
          >
            <span
              style={{
                fontFamily: theme.fonts.regular,
                fontSize: theme.fontSizes.normal,
                color: theme.colors.fontColor,
              }}
            >
              Class/Format
              <span> *</span>
            </span>

            <Stack.Item
              grow
              align="stretch"
              style={{ marginTop: "10px" }}
            ></Stack.Item>
            <Stack enableScopedSelectors>
              <Box sx={{ width: "100%" }}>
                <ClassFormatTreeLoad
                  onLoadingStateChange={onTreeDataLoadingChange}
                  showFieldValidation={showFieldValidation}
                  isLoadingOverride={isAutopopulating}
                />
              </Box>
            </Stack>
          </Stack.Item>

          <Stack.Item grow align="center" style={{ marginTop: "25px" }}>
            <Accordion
              style={accordianThemeStyle.accordianStyle}
              expanded={isAccordionExpanded}
              onChange={handleAccordionChange}
              onClick={(event) => event.stopPropagation()}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#4c8d6a" }} />}
                aria-controls="view-copy-content"
                id="view-copy-header"
                sx={getAccordionSummarySx(!!viewCopyError)}
              >
                <AccordionSummaryWithValidation
                  title="View Copy"
                  error={viewCopyError}
                  titleStyle={accordianThemeStyle.accordianTextStyle}
                />
              </AccordionSummary>
              <AccordionDetails style={accordianThemeStyle.accordianDetails}>
                <Grid
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "10px",
                    alignItems: "stretch",
                    flexDirection: "row",
                  }}
                >
                  {showAutoGeneratePanel && (
                    <Box
                      style={accordianThemeStyle.accordianBoxStyle}
                      sx={{
                        height: leftBoxHeight,
                        ...(bulkSettingsViewCopy && { flex: 1, width: "100%" }),
                      }}
                    >
                      <Box>
                        <Toggle
                          style={{ fontFamily: theme.fonts.regular }}
                          disabled={
                            bulkSettingsViewCopy ? false : createViewCopyDisable
                          }
                          checked={createViewCopy}
                          onChange={(ev, check) => {
                            if (
                              check === true &&
                              viewCopyFiles &&
                              viewCopyFiles.length > 0
                            ) {
                              onViewCopyUploadChange(
                                [],
                                0,
                                0,
                                viewRequired,
                                uploadFiles,
                              );
                            }
                            onCreateViewCopySelect(check);
                          }}
                          {...(!bulkSettingsViewCopy && {
                            onText: (
                              <div
                                style={{
                                  fontFamily: theme.fonts.regular,
                                  fontSize: theme.fontSizes.normal,
                                }}
                              >
                                {getViewCopyToolTip()}
                              </div>
                            ),
                            offText: (
                              <div
                                style={{
                                  fontFamily: theme.fonts.regular,
                                  fontSize: theme.fontSizes.normal,
                                }}
                              >
                                {getViewCopyToolTip()}
                              </div>
                            ),
                          })}
                        />
                      </Box>

                      <Typography style={accordianThemeStyle.viewCopyTextStyle}>
                        {FILE_AUTO_ENABLE}
                        <br />
                        {FILE_AUTO_ENABLE_SUB}
                      </Typography>
                    </Box>
                  )}
                  {!bulkSettingsViewCopy && (
                    <Box
                      sx={{
                        width: "100%",
                        height: rightBoxHeight,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Stack disableShrink style={{ height: "100%" }}>
                          <Stack.Item
                            styles={fileStyleTheme.ViewCopyStackStyle}
                            sx={{
                              flex: hasUploadedFiles ? 0.6 : 1,
                              transition: "flex 0.3s ease",
                              minHeight: uploadBoxHeight,
                            }}
                          >
                            <ViewCopyDragDropZone
                              isDisabled={isUploadDisabled()}
                              boxHeight={uploadBoxHeight}
                            />
                          </Stack.Item>
                          {hasUploadedFiles && (
                            <Stack.Item
                              sx={{
                                flex: 0.4,
                                transition: "flex 0.3s ease",
                              }}
                            >
                              <ViewCopyRecord />
                            </Stack.Item>
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Stack.Item>

          <Stack.Item grow align="center" style={{ marginTop: "10px" }}>
            <Accordion
              style={accordianThemeStyle.accordianStyle}
              expanded={objectConAccordionExpanded}
              onChange={handleObjectConAccordionChange}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#4c8d6a" }} />}
                aria-controls="object-connection-content"
                id="object-connection-header"
                sx={getAccordionSummarySx(!!objectConnectionError)}
              >
                <AccordionSummaryWithValidation
                  title="Object Connection(s)"
                  error={objectConnectionError}
                  titleStyle={accordianThemeStyle.accordianTextStyle}
                />
              </AccordionSummary>
              <AccordionDetails
                style={accordianThemeStyle.accordianDetails}
                sx={{ overflow: "hidden" }}
              >
                <Box sx={{ width: "100%", overflow: "hidden" }}>
                  <Tabs
                    value={objectConnectionTabKey}
                    onChange={(_e, v) => handleObjectConnectionTabChange(v)}
                    variant="fullWidth"
                    aria-label="Object connections"
                    sx={{
                      minHeight: 44,
                      marginBottom: "10px",
                      overflow: "visible",
                      "& .MuiTabs-indicator": {
                        backgroundColor: "#4c8d6a",
                        height: 3,
                      },
                      "& .MuiTab-root": {
                        overflow: "visible",
                        fontFamily: theme.fonts.regular,
                        fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                        textTransform: "none",
                        color: theme.colors.fontColor,
                        minHeight: 44,
                      },
                      "& .MuiTab-root.Mui-selected": {
                        fontWeight: 700,
                        color: "#4c8d6a",
                      },
                    }}
                  >
                    <Tab
                      value={tabKey.checkinNewSelectedObj}
                      sx={{ overflow: "visible" }}
                      label={
                        <Box
                          component="span"
                          sx={{
                            position: "relative",
                            display: "inline-block",
                            lineHeight: 1.2,
                          }}
                        >
                          <span>{`Selected (${selectedObjectConnectionCount})`}</span>
                          {showSelectedNewIndicator ? (
                            <Box
                              component="span"
                              sx={{
                                position: "absolute",
                                top: -6,
                                right: -12,
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                bgcolor: "#d32f2f",
                                pointerEvents: "none",
                              }}
                              aria-hidden
                            />
                          ) : null}
                        </Box>
                      }
                    />
                    <Tab
                      value={tabKey.checkinNewRecentObj}
                      label="Favorites"
                      icon={<Favorite sx={{ fontSize: 16 }} />}
                      iconPosition="end"
                    />
                    <Tab
                      value={tabKey.checkinNewAdvanceObj}
                      label="Search"
                      icon={<Search sx={{ fontSize: 16 }} />}
                      iconPosition="end"
                    />
                  </Tabs>
                  <Box
                    sx={{
                      position: "relative",
                      height: "200px",
                      minHeight: "200px",
                      maxHeight: "200px",
                      boxSizing: "border-box",
                      px: 1,
                      pb: 1,
                      overflowX: "hidden",
                      overflowY: "auto",
                      scrollbarWidth: "thin",
                      scrollbarColor: `${theme.colors?.scrollbarThumb ?? "#c4c4c4"} ${
                        theme.colors?.scrollbarBackground ?? "#f0f0f0"
                      }`,
                    }}
                  >
                    {objectConnectionTabKey === tabKey.checkinNewSelectedObj && (
                      <Box sx={{ position: "relative", width: "100%", zIndex: 1 }}>
                        <SelectedObjectConnection embedded />
                      </Box>
                    )}
                    {objectConnectionTabKey === tabKey.checkinNewRecentObj && (
                      <RecentObjectsView
                        embedded
                        onEmbeddedFavoriteAdded={() =>
                          setShowSelectedNewIndicator(true)
                        }
                      />
                    )}
                    {objectConnectionTabKey === tabKey.checkinNewAdvanceObj && (
                      <AdvSearchObjectConnectionTab embedded />
                    )}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Stack.Item>

          <Stack.Item
            grow
            align="center"
            style={{
              width: "100%",
              justifyItems: "center",
            }}
          >
            {/* {!omitOneDriveDeletionRow && (isAllowSelection || !!selectedDocumentFromList) && (
              <Box
                sx={{
                  mt: "10px",
                  paddingLeft: "5px",
                  paddingRight: "16px",
                  height: "43px",
                  backgroundColor: "#F0EEE2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "-webkit-fill-available",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Checkbox
                    checked={isDeleteOneDriveChecked}
                    onChange={handleDeleteOneDriveChange}
                    sx={{
                      color: "#AE9B2E",
                      "&.Mui-checked": {
                        color: "#AE9B2E",
                      },
                    }}
                  />

                  <Tooltip
                    title={DELETE_ONEDRIVE_DOC_TOOLTIP}
                    placement="top"
                    arrow
                  >
                    <Typography
                      sx={{
                        color: "#AE9B2E",
                        fontWeight: "bold",
                        fontFamily: theme.fonts?.regular,
                        fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                        cursor: "default",
                      }}
                    >
                      {DELETE_ONEDRIVE_DOC}
                    </Typography>
                  </Tooltip>
                </Box>

                <Box
                  component="img"
                  src={docDelete}
                  alt="icon"
                  sx={{ width: 30, height: 30 }}
                />
              </Box>
            )} */}

            <Box
              sx={{
                mt: "10px",
                paddingLeft: "5px",
                paddingRight: "16px",
                height: "43px",
                backgroundColor: "#E6F4EA",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "-webkit-fill-available",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Checkbox
                  checked={isChecked}
                  onChange={handleReleaseChange}
                  sx={{
                    color: "#4c8d6a",
                    "&.Mui-checked": {
                      color: "#4c8d6a",
                    },
                  }}
                />
                <Tooltip title={RELEASE_DOCUMENT_TOOLTIP} placement="top" arrow>
                  <Typography
                    sx={{
                      color: "#4c8d6a",
                      fontWeight: "bold",
                      fontFamily: theme.fonts?.regular,
                        fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                      cursor: "default",
                    }}
                  >
                    {RELEASE_DOCUMENT}
                  </Typography>
                </Tooltip>
              </Box>

              <Box
                component="img"
                src={docChecked}
                alt="icon"
                sx={{ width: 25, height: 25 }}
              />
            </Box>
          </Stack.Item>
        </Stack.Item>
      </Stack>
    </>
  );
};

export default DocumentBasicItem;
